import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import DataTables from "../../Components/Tables/DataTable";
import { sentenceCase, titleCase } from "../../utils/case";
import { getUsers, deleteUser, updateUser } from "../../api/user.api";
import {
  Button,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  ButtonGroup,
  DropdownToggle,
} from "reactstrap";
import { Edit, Key, Trash2 } from "react-feather";
import { toast } from "react-toastify";
import { hasRole } from "../../utils/auth";

const UserMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = hasRole(
    "superadmin",
    "finance"
  );
  const [data, setData] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  // reset password modal
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    perPage: 10,
    totalPages: 1,
  });

  const fetchUsers = async (page = 1, perPage = 10) => {
    try {
      const res = await getUsers({ page, perPage });

      setData(res?.data || []);
      setMeta(
        res?.meta || {
          total: 0,
          page,
          perPage,
          totalPages: 1,
        }
      );
    } catch (err) {
      console.error(err.message);
    }
  };

  useEffect(() => {
    fetchUsers(meta.page, meta.perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    navigate("/users/create");
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(t("confirm delete"));
    if (!confirmed) return;

    try {
      setDeletingId(row.id);
      await deleteUser(row.id);
      toast.success(t("deleted successfully"));
      fetchUsers(meta.page, meta.perPage);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleRoleChange = async (row, newRole) => {
    if (row.role === newRole) return;

    const confirmed = window.confirm(
      `${titleCase(t("confirm role change"))}: ${titleCase(row.role)} → ${titleCase(newRole)}`
    );

    if (!confirmed) return;

    try {
      setUpdatingRoleId(row.id);

      await updateUser(row.id, {
        ...row,
        role: newRole,
      });

      toast.success(t("updated successfully"));
      fetchUsers(meta.page, meta.perPage);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleStatusToggle = async (row) => {
    const newStatus = row.status === "active" ? "inactive" : "active";

    const confirmed = window.confirm(
      `${sentenceCase(t("confirm status"))}: ${titleCase(row.status)} → ${titleCase(newStatus)}`
    );

    if (!confirmed) return;

    try {
      setUpdatingStatusId(row.id);

      await updateUser(row.id, {
        ...row,
        status: newStatus,
      });

      toast.success(t("updated successfully"));
      fetchUsers(meta.page, meta.perPage);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const openResetModal = (row) => {
    setSelectedUser(row);
    setNewPassword("");
    setResetModalOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword) {
      toast.error(t("password required"));
      return;
    }

    try {
      await updateUser(selectedUser.id, {
        ...selectedUser,
        password: newPassword,
      });

      toast.success(t("password updated successfully"));
      setResetModalOpen(false);
      setSelectedUser(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePageChange = (newPage) => {
    fetchUsers(newPage, meta.perPage);
  };

  const handlePerPageChange = (newPerPage) => {
    fetchUsers(1, newPerPage);
  };

  const action = (
    canManage && (
      <Button color="success" onClick={handleCreate}>
        {t("create").toUpperCase()}
      </Button>
    )
  );

  const columns = [
    {
      name: titleCase(t("name")),
      selector: (row) => row.name,
      sortable: true,
      wrap: true,
    },
    {
      name: titleCase(t("username")),
      selector: (row) => row.username,
      sortable: true,
    },
    {
      name: titleCase(t("email")),
      selector: (row) => row.email,
      wrap: true,
    },
    {
      name: titleCase(t("status")),
      center: true,
      cell: (row) => (
        <div className="d-flex justify-content-center w-100">
          <Label className="switch mb-0">
            <Input
              type="checkbox"
              checked={row.status === "active"}
              disabled={updatingStatusId === row.id}
              onChange={() => handleStatusToggle(row)}
              disabled={!canManage}
            />
            <span
              className={`${
                row.status === "active" ? "bg-success" : "bg-light"
              } switch-state`}
            ></span>
          </Label>
        </div>
      ),
    },
    {
      name: titleCase(t("role")),
      cell: (row) => {
        const isOpen = openDropdownId === row.id;

        const items = [
          { id: "superadmin", item: titleCase(t("superadmin")) },
          { id: "operation", item: titleCase(t("operation")) },
          { id: "finance", item: titleCase(t("finance")) },
          { id: "security", item: titleCase(t("security")) },
        ];

        const toggle = () => {
          setOpenDropdownId(isOpen ? null : row.id);
        };

        return (
          <Dropdown isOpen={isOpen} toggle={toggle}>
            <DropdownToggle
              tag="div"
              className="dropbtn btn-round rounded-pill px-2 py-1 d-inline-flex align-items-center bg-info"
              style={{
                cursor: "pointer",
                color: "#fff",
                fontSize: "0.8rem",
              }}
              disabled={!canManage}
            >
              {titleCase(row.role)}
              <span className="ms-1">
                <i className="icofont icofont-arrow-down" />
              </span>
            </DropdownToggle>

            <DropdownMenu style={{ minWidth: "120px" }}>
              {items.map((itm) => (
                <DropdownItem
                  key={itm.id}
                  onClick={() => {
                    setOpenDropdownId(null);
                    handleRoleChange(row, itm.id);
                  }}
                  style={{
                    fontSize: "0.85rem",
                    padding: "6px 10px",
                  }}
                >
                  {itm.item}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        );
      },
    },
  ];
  if (canManage) {
    columns.push({
      name: titleCase(t("action")),
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <Edit
            size={18}
            style={{ cursor: "pointer", color: "yellow", margin: 5 }}
            onClick={() => navigate(`/users/edit/${row.id}`)}
            title={t("edit user")}
          />

          <Key
            size={18}
            style={{ cursor: "pointer", color: "blue", margin: 5 }}
            onClick={() => openResetModal(row)}
            title={t("reset password")}
          />

          <Trash2
            size={18}
            style={{
              cursor: deletingId === row.id ? "not-allowed" : "pointer",
              color: "red",
              margin: 5,
              opacity: deletingId === row.id ? 0.5 : 1,
            }}
            onClick={() => handleDelete(row)}
            title={t("delete")}
          />
        </div>
      ),
    });
  }

  return (
    <>
      <DataTables
        parent={titleCase(t("user"))}
        title={titleCase(t("user management"))}
        mainTitle={titleCase(t("user management"))}
        headerCardTitle={titleCase(t("user list"))}
        initialData={data}
        columns={columns}
        action={action}
        meta={meta}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
      />

      {/* Reset Password Modal */}
      <Modal
        isOpen={resetModalOpen}
        toggle={() => setResetModalOpen(!resetModalOpen)}
      >
        <ModalHeader toggle={() => setResetModalOpen(!resetModalOpen)}>
          {titleCase(t("reset password"))}
        </ModalHeader>
        <ModalBody>
          <Label>{titleCase(t("new password"))}</Label>
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={sentenceCase(t("enter new password"))}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleResetPassword}>
            {titleCase(t("confirm"))}
          </Button>
          <Button color="secondary" onClick={() => setResetModalOpen(false)}>
            {titleCase(t("cancel"))}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default UserMain;